import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Valid workspace modes.
 *
 * `code` was the Tasks surface. Tasks has moved to the left-nav More menu and
 * is no longer reachable from the workspace top nav, but the mode value is
 * preserved here so any deep-links in the wild with `?mode=code` continue to
 * validate. They'll render the chat surface as a safe fallback since the
 * render switch below no longer matches `code`.
 *
 * `workflows` is the new top-nav module and replaces Tasks as the third tab.
 */
// "cowork" (a read-only issues board with no way in) was removed 2026-09-26;
// an old ?mode=cowork link now falls back to chat.
export type WorkspaceMode = "chat" | "org" | "code" | "workflows" | "tasks";

interface ThreadContext {
  agentId?: string;
  sessionId?: string;
  channelId?: string;
  issueId?: string;
}

interface WorkspaceContextValue {
  mode: WorkspaceMode;
  setMode: (mode: WorkspaceMode) => void;
  threadContext: ThreadContext;
  updateThreadContext: (ctx: Partial<ThreadContext>) => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

const validModes: WorkspaceMode[] = ["chat", "org", "code", "workflows", "tasks"];
const normalizeChannelId = (channelId: string | null | undefined) =>
  channelId && channelId !== "general" ? channelId : undefined;

/** Thread keys mirrored into the URL, in a stable order. */
const THREAD_KEYS = ["agentId", "sessionId", "channelId", "issueId"] as const;

export function WorkspaceProvider({
  children,
  embedded = false,
  initialMode = "chat",
  initialThreadContext,
}: {
  children: React.ReactNode;
  embedded?: boolean;
  initialMode?: WorkspaceMode;
  initialThreadContext?: ThreadContext;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [embeddedMode, setEmbeddedMode] = useState<WorkspaceMode>(initialMode);

  // Derive `mode` straight from the URL rather than seeding it once into local
  // state. This keeps the top-nav tab in lockstep with sidebar navigation:
  // when the sidebar navigates to a chat it builds a fresh URL without `mode`,
  // so mode falls back to "chat" and the view switches back automatically.
  const urlMode = searchParams.get("mode") as WorkspaceMode | null;
  const mode: WorkspaceMode = embedded
    ? embeddedMode
    : urlMode && validModes.includes(urlMode)
      ? urlMode
      : "chat";

  const setMode = useCallback(
    (next: WorkspaceMode) => {
      if (embedded) {
        setEmbeddedMode(next);
        return;
      }
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          params.set("mode", next);
          return params;
        },
        { replace: true },
      );
    },
    [embedded, setSearchParams],
  );

  // ---------------------------------------------------------------------
  // The URL search params are the SINGLE SOURCE OF TRUTH for thread context
  // when not embedded. `threadContext` is DERIVED from them, never mirrored
  // into local state.
  //
  // The previous shape kept a useState seeded once from the URL and
  // reconciled by an effect, which left state exactly one render behind the
  // URL. Any updateThreadContext({ sessionId }) landing inside that window
  // wrote the WHOLE merged context back — dragging a stale agentId into the
  // URL via replaceState. That fought the pushState from the agent picker
  // and ping-ponged unbounded (39k history calls in one session, ~1,170/sec).
  //
  // Two rules keep it closed now:
  //   1. Reads derive from the URL, so nothing can hold a stale agent id.
  //   2. Writes touch ONLY the keys the caller actually passed, and bail
  //      when the resulting query string is unchanged.
  // ---------------------------------------------------------------------
  const [embeddedThread, setEmbeddedThread] = useState<ThreadContext>(() => ({
    agentId: initialThreadContext?.agentId ?? searchParams.get("agentId") ?? undefined,
    sessionId: initialThreadContext?.sessionId ?? searchParams.get("sessionId") ?? undefined,
    channelId: normalizeChannelId(
      initialThreadContext?.channelId ?? searchParams.get("channelId"),
    ),
    issueId: initialThreadContext?.issueId ?? searchParams.get("issueId") ?? undefined,
  }));

  const urlThread = useMemo<ThreadContext>(
    () => ({
      agentId: searchParams.get("agentId") ?? undefined,
      sessionId: searchParams.get("sessionId") ?? undefined,
      channelId: normalizeChannelId(searchParams.get("channelId")),
      issueId: searchParams.get("issueId") ?? undefined,
    }),
    [searchParams],
  );

  const threadContext = embedded ? embeddedThread : urlThread;

  // Latest params, readable from a stable callback without putting
  // searchParams in its dependency list (which would change the callback's
  // identity on every URL change and re-fire every consumer effect).
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  // Legacy deep-links used ?channelId=general to mean "no channel". Strip it
  // once so it can't linger as a phantom filter.
  useEffect(() => {
    if (embedded) return;
    if (searchParams.get("channelId") !== "general") return;
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        params.delete("channelId");
        return params;
      },
      { replace: true },
    );
  }, [embedded, searchParams, setSearchParams]);

  const updateThreadContext = useCallback(
    (ctx: Partial<ThreadContext>) => {
      if (embedded) {
        setEmbeddedThread((prev) => ({
          ...prev,
          ...ctx,
          ...(ctx.channelId === "general" ? { channelId: undefined } : {}),
        }));
        return;
      }

      // Only the keys present on `ctx` are written. A caller changing
      // sessionId must never restate agentId — that was the loop.
      const applyTo = (current: URLSearchParams) => {
        const params = new URLSearchParams(current);
        for (const key of THREAD_KEYS) {
          if (!(key in ctx)) continue;
          const raw = ctx[key];
          const value = key === "channelId" ? normalizeChannelId(raw) : raw || undefined;
          if (value) params.set(key, value);
          else params.delete(key);
        }
        return params;
      };

      // Equality guard: a no-op update must not reach the history stack at
      // all. On its own this stops a runaway loop from being unbounded.
      const current = searchParamsRef.current;
      const next = applyTo(current);
      if (next.toString() === current.toString()) return;

      // Keep the ref in step so two updates in the same tick compose
      // instead of the second one overwriting the first.
      searchParamsRef.current = next;
      setSearchParams((live) => applyTo(live), { replace: true });
    },
    [embedded, setSearchParams],
  );

  return (
    <WorkspaceContext.Provider value={{ mode, setMode, threadContext, updateThreadContext }}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace must be used within a WorkspaceProvider");
  return ctx;
}
